// ═══════════════════════════════════════════════════════════════════════════
// COMPOSANT — Analyse de CR (parsing .docx/.pptx + IA + création d'actions)
// ═══════════════════════════════════════════════════════════════════════════
//
// Factorisation du traitement "Analyser CR" partagé entre :
//   • l'onglet « Générer les actions » d'une réunion (page-meeting.ts)
//   • le wizard « Analyser un compte-rendu » (page-meeting-wizard.ts)
//
// Les deux flux suivent la même séquence — parse → analyse Claude → création
// séquentielle des actions — mais diffèrent sur le TYPE d'action créée :
//   • Onglet réunion : drafts (`to_validate=True`) liés à la réunion.
//   • Wizard standalone : actions normales avec project_ids/committee_ids.
//
// La fonction publique `runCrAnalysis` reçoit donc un callback `createAction`
// qui encapsule cette différence côté appelant. L'overlay de progression est
// fourni aussi pour éviter une duplication HTML/CSS.

export interface CrAnalysisCallbacks {
  setStep(msg: string): void;
}

export interface RunCrAnalysisOptions {
  // Source du CR : un fichier à parser, OU du texte déjà brut (exactement
  // l'un des deux ; `file` gagne si les deux sont fournis).
  file?: File;
  text?: string;
  callbacks: CrAnalysisCallbacks;
  createAction(action: CrProposedAction): Promise<{ id: number } | null | void>;
}

export interface CrAnalysisResult {
  done: number;
  total: number;
  createdIds: number[];
  truncated: boolean;
}

declare global {
  // Action proposée par l'analyse de CR, enrichie : date normalisée (D),
  // porteurs résolus en user_ids = RACI Acteur (C), priorité, et extrait source
  // du CR (B). Type GLOBAL → visible des wizards sans import ESM (les modules
  // se chargent par <script> séquentiels, pas en ESM).
  interface CrProposedAction {
    text: string;
    echeanceIso: string | null;
    performers: number[];
    priority: number;
    citationSource: string;
  }
  interface Window {
    componentCrAnalysis: {
      runCrAnalysis: (opts: RunCrAnalysisOptions) => Promise<CrAnalysisResult>;
      showAnalyseOverlay: (initialMsg: string, title?: string) => HTMLElement;
      crSourceBadgeHtml: (actionId: number) => string;
      forgetCrSource: (actionId: number) => void;
    };
  }
}

(function () {
  // ── Provenance des actions extraites ──────────────────────────────────────
  // Extrait du CR (`citation_source`) ayant justifié chaque action, indexé par
  // id d'action créée. Volontairement NON persisté (décision 2026-08-07) : la
  // provenance ne sert qu'à arbitrer avant publication. Conséquence assumée :
  // la pastille disparaît au rechargement de la page.
  //
  // Le magasin vit ici et non dans les pages appelantes : `runCrAnalysis` est
  // le seul endroit qui voit à la fois la citation et l'id de l'action créée,
  // donc le seul endroit où l'association peut être faite sans duplication.
  const _citationByActionId: Record<number, string> = {};

  function rememberCrSource(actionId: number, citation: string): void {
    const c = (citation || '').trim();
    if (actionId && c) _citationByActionId[actionId] = c;
  }

  function forgetCrSource(actionId: number): void {
    delete _citationByActionId[actionId];
  }

  // Pastille « ? » cliquable-au-survol posée devant les CTAs d'une ligne
  // d'action brouillon. Chaîne vide si aucune provenance connue pour cet id
  // (action saisie à la main, ou page rechargée depuis l'analyse).
  function crSourceBadgeHtml(actionId: number): string {
    const citation = _citationByActionId[actionId];
    if (!citation) return '';
    ensureSourceBadgeStyles();
    // Pas de `esc()` ici : t() échappe déjà les valeurs interpolées (cf.
    // core.ts). Un second passage afficherait « &quot; » dans l'infobulle
    // dès que l'extrait du CR contient des guillemets — c'est fréquent.
    const label = t('crAnalysis.source.tooltip', { citation });
    return (
      `<span class="cmp-cr-source" tabindex="0" role="note" title="${label}"` +
      ` aria-label="${label}">?</span>`
    );
  }

  // Style de la pastille : cercle bordé plutôt qu'un « ? » nu — l'ancienne
  // version (texte gris clair sans contour) passait inaperçue à côté des CTAs.
  function ensureSourceBadgeStyles(): void {
    if (document.getElementById('cmp-cr-source-styles')) return;
    const s = document.createElement('style');
    s.id = 'cmp-cr-source-styles';
    s.textContent = `
      .cmp-cr-source {
        display:inline-flex;
        align-items:center;
        justify-content:center;
        width:18px;height:18px;
        margin-right:8px;
        border:1px solid var(--border2);
        border-radius:50%;
        font-size:11px;
        font-weight:700;
        line-height:1;
        color:var(--text2);
        background:var(--bg2);
        cursor:help;
        vertical-align:middle;
      }
      .cmp-cr-source:hover, .cmp-cr-source:focus {
        color:var(--accent);
        border-color:var(--accent);
        outline:none;
      }
    `;
    document.head.appendChild(s);
  }

  function ensureOverlayStyles(): void {
    if (document.getElementById('cmp-cr-analyse-overlay-styles')) return;
    const s = document.createElement('style');
    s.id = 'cmp-cr-analyse-overlay-styles';
    s.textContent = `
      @keyframes cmp-cr-spin { to { transform: rotate(360deg); } }
      .cmp-cr-spinner {
        display:inline-block;
        width:36px;height:36px;
        border:3px solid var(--border2);
        border-top-color:var(--accent);
        border-radius:50%;
        animation: cmp-cr-spin 0.7s linear infinite;
      }
    `;
    document.head.appendChild(s);
  }

  // Overlay modal non dismissable : fond assombri + spinner + libellé d'étape
  // que l'appelant peut mettre à jour via la classe `.cmp-cr-step`.
  function showAnalyseOverlay(initialMsg: string, title?: string): HTMLElement {
    ensureOverlayStyles();
    const overlay = document.createElement('div');
    overlay.className = 'modal-overlay cmp-cr-overlay';
    overlay.style.cssText =
      'position:fixed;inset:0;background:rgba(0,0,0,0.4);display:flex;align-items:center;justify-content:center;z-index:9999';
    const titleHtml = title
      ? `<h3 style="margin:0 0 14px 0;font-size:15px">${esc(title)}</h3>`
      : '';
    overlay.innerHTML = `
      <div class="modal" style="padding:28px 32px;text-align:center;max-width:420px;background:var(--bg);border-radius:var(--radius);box-shadow:0 8px 32px rgba(0,0,0,0.2)">
        ${titleHtml}
        <div style="display:flex;justify-content:center;margin-bottom:14px">
          <span class="cmp-cr-spinner"></span>
        </div>
        <p class="cmp-cr-step" style="margin:0;font-size:13px;color:var(--text2);min-height:18px">${esc(initialMsg)}</p>
      </div>`;
    document.body.appendChild(overlay);
    return overlay;
  }

  // Pipeline complet d'analyse : parsing → IA → création.
  // L'appelant fournit `createAction(text)` qui décide où l'action atterrit
  // (draft de réunion ou action globale). Les erreurs de création par item
  // sont silencieusement loguées (warn) pour ne pas bloquer la suite — le
  // compteur `done` final reflète le nombre réel de succès.
  //
  // Source : soit un fichier (.docx/.pptx, passé au parseur), soit du texte
  // déjà brut (CR collé dans le wizard). Les deux convergent dès l'étape 2 —
  // la branche « texte collé » du wizard dupliquait sinon tout le pipeline.
  async function runCrAnalysis(opts: RunCrAnalysisOptions): Promise<CrAnalysisResult> {
    const { file, callbacks, createAction } = opts;

    // Étape 1 : parsing .docx / .pptx → texte brut (sauté si texte fourni).
    let text = (opts.text || '').trim();
    let truncated = false;
    if (file) {
      callbacks.setStep(t('pageMeeting.generation.analyse.parsing'));
      const parsed = (await api.parseDocx(file)) as { text?: string; truncated?: boolean };
      text = (parsed.text || '').trim();
      truncated = !!parsed.truncated;
    }
    if (!text) {
      throw new Error(t('pageMeeting.generation.analyse.noActions'));
    }

    // Étape 2 : extraction des actions par Claude.
    callbacks.setStep(t('pageMeeting.generation.analyse.analyzing'));
    const analyzed = (await api.analyzeMeeting(text)) as {
      actions?: Array<{
        action?: string;
        echeance_iso?: string | null;
        performers?: number[];
        priority?: number;
        citation_source?: string;
      }>;
    };
    const list: CrProposedAction[] = (analyzed.actions || [])
      .map((a) => ({
        text: (a.action || '').trim(),
        echeanceIso: a.echeance_iso || null,
        performers: Array.isArray(a.performers) ? a.performers : [],
        priority: typeof a.priority === 'number' ? a.priority : 2,
        citationSource: (a.citation_source || '').trim(),
      }))
      .filter((a) => a.text.length > 0);
    if (!list.length) {
      return { done: 0, total: 0, createdIds: [], truncated };
    }

    // Étape 3 : création séquentielle. On reste séquentiel pour ne pas saturer
    // le backend et pour conserver l'ordre des actions tel qu'extrait.
    let done = 0;
    const createdIds: number[] = [];
    for (const action of list) {
      callbacks.setStep(
        t('pageMeeting.generation.analyse.creating', {
          done,
          total: list.length,
        }),
      );
      try {
        const created = await createAction(action);
        done += 1;
        if (created && typeof created === 'object' && 'id' in created && created.id) {
          createdIds.push(created.id as number);
          // Provenance mémorisée ici pour les DEUX écrans appelants : c'est le
          // seul point du flux qui connaît l'extrait du CR et l'id créé.
          rememberCrSource(created.id as number, action.citationSource);
        }
      } catch (err) {
        safeWarn('[cr-analysis] création KO', err);
      }
    }
    return { done, total: list.length, createdIds, truncated };
  }

  window.componentCrAnalysis = {
    runCrAnalysis,
    showAnalyseOverlay,
    crSourceBadgeHtml,
    forgetCrSource,
  };
})();

export {};
